import type { StatementPeriod } from '../types/dealerFundStatementPeriod.types';

export const STATEMENT_MONTH_OPTIONS = 24;
export const MAX_RANGE_DAYS_INCLUSIVE = 366;

const DAY_MS = 86_400_000;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

const dayNumber = (iso: string): number | null => {
    const match = ISO_DATE.exec(iso);
    if (!match) return null;
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const time = Date.UTC(year, month - 1, day);
    const check = new Date(time);
    const valid = check.getUTCFullYear() === year && check.getUTCMonth() === month - 1 && check.getUTCDate() === day;
    return valid ? time / DAY_MS : null;
};

export const utcTodayIsoDate = (now: Date): string =>
    `${pad(now.getUTCFullYear(), 4)}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())}`;

const shiftMonth = (month: string, delta: number): string => {
    const [year, number] = month.split('-').map(Number);
    const index = year * 12 + (number - 1) + delta;
    return `${pad(Math.floor(index / 12), 4)}-${pad((index % 12) + 1)}`;
};

export const previousMonth = (todayIso: string): string => shiftMonth(todayIso.slice(0, 7), -1);

export const recentMonths = (todayIso: string, count: number): string[] => {
    const current = todayIso.slice(0, 7);
    return Array.from({ length: count }, (_, offset) => shiftMonth(current, -offset));
};

export const periodQueryParams = (period: StatementPeriod): Record<string, string> =>
    period.kind === 'month' ? { month: period.month } : { from: period.from, to: period.to };

export const periodKey = (period: StatementPeriod): string =>
    period.kind === 'month' ? `month:${period.month}` : `range:${period.from}:${period.to}`;

export const openPeriodDay = (period: StatementPeriod, todayIso: string): string | null => {
    const reachesToday = period.kind === 'month' ? period.month >= todayIso.slice(0, 7) : period.to >= todayIso;
    return reachesToday ? todayIso : null;
};

export const validateRange = (from: string | null, to: string | null, todayIso: string): string | null => {
    if (!from || !to) return 'Choose both a start and an end date.';
    const fromDay = dayNumber(from);
    const toDay = dayNumber(to);
    const todayDay = dayNumber(todayIso);
    if (fromDay === null || toDay === null || todayDay === null) return 'Choose valid dates.';
    if (fromDay > toDay) return 'The start date must not be after the end date.';
    if (toDay > todayDay) return 'The end date must not be in the future.';
    if (toDay - fromDay + 1 > MAX_RANGE_DAYS_INCLUSIVE) {
        return `The range must not be longer than ${MAX_RANGE_DAYS_INCLUSIVE} days.`;
    }
    return null;
};

export const isoDateFromPickerDate = (date: Date): string =>
    `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const pickerDateFromIsoDate = (iso: string): Date => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day);
};
